import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  CheckCircle2, 
  Clock, 
  ChevronRight,
  User,
  FileSignature,
  Upload,
  FileText,
  Shield,
  AlertCircle
} from 'lucide-react';

const PendingActionsList = ({ user, commitments = [], signatureRequests = [] }) => {
  // Don't show for admins
  const isAdmin = ['super_admin', 'fund_director', 'analyst', 'compliance'].includes(user?.role);
  if (isAdmin) return null;

  const kycStatus = user?.verification_status;
  const profileCompletion = user?.profile_completion || 0;
  
  // Build list of pending actions
  const pendingActions = [];

  // 1. Profile completion
  if (profileCompletion < 100) {
    pendingActions.push({
      id: 'profile',
      icon: User,
      title: 'Completar Perfil',
      subtitle: `${profileCompletion}% completado`,
      link: '/app/profile',
      priority: kycStatus !== 'approved' ? 'high' : 'low'
    });
  }

  // 2. KYC Status
  if (kycStatus === 'pending') {
    pendingActions.push({
      id: 'kyc',
      icon: Shield,
      title: 'Verificación KYC',
      subtitle: 'En revisión por el equipo',
      link: '/app/profile',
      priority: 'medium',
      status: 'waiting'
    });
  }

  // 3. Pending signatures
  const pendingSignatures = signatureRequests.filter(s => ['pending', 'viewed'].includes(s.status));
  const awaitingSignatureCommitments = commitments.filter(c => c.status === 'awaiting_signature');
  
  if (pendingSignatures.length > 0) {
    pendingSignatures.forEach(sig => {
      pendingActions.push({
        id: `sig-${sig.request_id}`,
        icon: FileSignature,
        title: sig.template_name || 'Documento pendiente',
        subtitle: sig.opportunity_title ? `Proyecto: ${sig.opportunity_title}` : 'Requiere firma',
        link: `/app/signatures/${sig.request_id}`,
        priority: 'high'
      });
    });
  } else if (awaitingSignatureCommitments.length > 0) {
    awaitingSignatureCommitments.forEach(commit => {
      pendingActions.push({
        id: `commit-sig-${commit.commitment_id}`,
        icon: FileSignature,
        title: 'Acuerdo de Inversión',
        subtitle: commit.opportunity_title || 'Requiere firma',
        link: commit.signature_request_id ? `/app/signatures/${commit.signature_request_id}` : '/app/signatures',
        priority: 'high'
      });
    });
  }

  // 4. Approved commitments awaiting funds
  const awaitingFunding = commitments.filter(c => c.status === 'approved' && !c.proof_of_transfer_doc_id);
  awaitingFunding.forEach(commit => {
    pendingActions.push({
      id: `funding-${commit.commitment_id}`,
      icon: Upload,
      title: 'Comprobante de Fondeo',
      subtitle: commit.opportunity_title || 'Inversión aprobada',
      link: '/app/investments',
      priority: 'high'
    });
  });

  // Sort by priority
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  pendingActions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  if (pendingActions.length === 0) {
    return (
      <Card className="card-dashboard border-emerald-500/20">
        <CardContent className="p-6 text-center">
          <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-3" />
          <p className="text-sm font-medium">¡Todo en orden!</p>
          <p className="text-xs text-muted-foreground mt-1">No tiene acciones pendientes.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-dashboard">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          Acciones Pendientes
          <Badge variant="outline" className="ml-auto bg-amber-500/10 text-amber-500 border-amber-500/20">
            {pendingActions.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {pendingActions.slice(0, 5).map((action) => {
          const Icon = action.icon;
          return (
            <Link 
              key={action.id} 
              to={action.link}
              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors group"
            >
              <div className={`p-2 rounded-lg ${
                action.priority === 'high' ? 'bg-amber-500/10' : 
                action.priority === 'medium' ? 'bg-blue-500/10' : 'bg-muted'
              }`}>
                <Icon className={`h-4 w-4 ${
                  action.priority === 'high' ? 'text-amber-500' : 
                  action.priority === 'medium' ? 'text-blue-500' : 'text-muted-foreground'
                }`} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{action.title}</p>
                <p className="text-xs text-muted-foreground truncate">{action.subtitle}</p>
              </div>
              {action.status === 'waiting' ? (
                <Clock className="h-4 w-4 text-blue-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
              )}
            </Link>
          );
        })}
        
        {pendingActions.length > 5 && (
          <p className="text-xs text-muted-foreground text-center pt-2">
            +{pendingActions.length - 5} más
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingActionsList;
